'use client';

import { Timeline, TimelineContent, TimelineItem, TimelinePoint } from 'flowbite-react';
import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useState } from 'react';

import { Button } from '../button/button';
import { Flexbox } from '../flexbox/flexbox';
import { Paragraph } from '../paragraph/paragraph';
import { convertDate } from '../utils';
import type { HistoryItem, HistoryProps } from './types';

const customTheme = {
	root: {
		direction: {
			vertical: 'relative pl-2.5 w-full',
		},
	},
	item: {
		root: {
			vertical: 'mb-[10px] flex items-start relative',
		},
		content: {
			root: {
				base: 'w-full',
			},
			time: {
				base: 'text-sm font-normal text-n500_light_text',
			},
			title: {
				base: 'text-base font-normal text-black',
			},
		},
		point: {
			marker: {
				base: {
					vertical:
						'relative h-[9px] w-[9px] rounded-full border border-black bg-white dark:border-n700_text_dark_mode dark:bg-card_dark mr-3 mt-2',
				},
			},
			vertical:
				'before:absolute before:top-3.5 before:left-[4px] before:w-px before:h-full before:bg-dark dark:before:bg-n700_text_dark_mode',
		},
	},
};

export const History: FunctionComponent<HistoryProps> = ({
	horizontal = false,
	historyItems,
	icon,
	titleVariant = 'text-base-semibold',
	timeVariant = 'text-sm-semibold',
	onHistoryItemAction,
}) => {
	const t = useTranslations();
	const [openedIndex, setOpenedIndex] = useState<number | null>(null);

	const handleOpen = (item: HistoryItem) => {
		setOpenedIndex(null);
		onHistoryItemAction(item);
	};

	const renderHistoryItems = historyItems.map((item, index) => {
		const { time, title, isUpdated, id, documentFile } = item;
		const isOpen = openedIndex === index;
		const modifiedBy = documentFile?.modifiedBy ? `by ${documentFile?.modifiedBy}` : '';

		return (
			<TimelineItem key={id} className={isOpen ? 'bg-n50_hover dark:bg-n600_text' : ''}>
				<TimelinePoint icon={icon} />
				<TimelineContent>
					<Flexbox items='baseline'>
						<Paragraph
							onClick={() => setOpenedIndex(isOpen ? null : index)}
							variant={titleVariant}
							className='hover:underline cursor-pointer mr-2'
						>
							{title}
						</Paragraph>
						{isOpen && (
							<>
								<div className='w-fit'>
									<Button variant='text' onClick={() => handleOpen(item)} className='text-accent py-0 px-1 hover:underline'>
										{t('T_OPEN').toUpperCase()}
									</Button>
								</div>
								<span className='text-nowrap text-sm text-n500_light_text'>{!documentFile?.isLatest && t('T_WILL_CREATE_A_COPY')}</span>
							</>
						)}
					</Flexbox>
					<Paragraph className='text-n500_light_text' variant={timeVariant}>
						{`${convertDate(time)} - ${isUpdated ? `${t('T_UPDATED')} ${modifiedBy}` : `${t('T_CREATED')} ${modifiedBy}`}`}
					</Paragraph>
				</TimelineContent>
			</TimelineItem>
		);
	});

	return (
		<Timeline horizontal={horizontal} theme={customTheme}>
			{renderHistoryItems}
		</Timeline>
	);
};
